import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../../config/authConfig';
import { useAuth } from '../../contexts/AuthContext';
import { authenticationService } from '../../services/authentication.service';

function getRoleBasedDashboard(roles: string[]): string {
  const isSuperAdmin = roles.some(r => r === 'PLATFORM_SUPER_ADMIN' || r.toUpperCase().includes('SUPER_ADMIN'));
  // PLATFORM_SUPER_ADMIN defaults to the Admin (DSR user management) dashboard.
  if (isSuperAdmin) return '/admin/dashboard';
  // DSR_ADMIN must be checked before the generic ADMIN match ("DSR_ADMIN".includes('ADMIN') is true)
  const isDsrAdmin = roles.some(r => r === 'DSR_ADMIN');
  if (isDsrAdmin) return '/admin/dsr-admins';
  const isAdmin = roles.some(r => r === 'ADMIN' || r.toUpperCase().includes('ADMIN'));
  if (isAdmin) return '/admin/dashboard';
  const isServiceAgent = roles.some(r => r === 'SERVICE_AGENT' || r.toUpperCase() === 'SERVICE_AGENT');
  if (isServiceAgent) return '/service-agent/dashboard';
  return '/investor/dashboard';
}

const LoginCallback: React.FC = () => {


  console.log('[LoginCallback] ===== COMPONENT MOUNTING ===== URL:', window.location.href);
  
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const processedRef = React.useRef(false);

  console.log('[LoginCallback] RENDER - inProgress:', inProgress, 'isAuthenticated:', isAuthenticated, 'processedRef:', processedRef.current, 'URL:', window.location.href);

  useEffect(() => {
    console.log('[LoginCallback] useEffect START - processedRef:', processedRef.current, 'inProgress:', inProgress, 'isAuthenticated:', isAuthenticated);

    // MSAL silent token refresh loads the redirectUri inside a hidden iframe.
    // Because our redirectUri is the SPA entry, the whole React app — including this
    // component — mounts inside that iframe and would call acquireTokenSilent recursively,
    // tripping MSAL's `block_iframe_reload` guard. Detect the iframe and bail; MSAL's own
    // listeners will still read the response from this URL.
    if (typeof window !== 'undefined' && window.self !== window.top) {
      console.log('[LoginCallback] Inside MSAL iframe — skipping callback logic.');
      return;
    }

    if (processedRef.current) {
      console.log('[LoginCallback] Already processed, skipping');
      return;
    }

    // Return from the SET-PASSWORD flow (product decision 2026-09-08): B2C's reset-password
    // policy hands back sign-in tokens, so MSAL builds an account and the authenticated path
    // below would exchange them for a Facilon JWT and drop the user on the dashboard. After
    // setting a password the user must sign in with it on the B2C page instead.
    //
    // The URL check further down only fires when MSAL could NOT build an account; the
    // localStorage marker set by InvestorSetPassword before the redirect is the reliable
    // signal, and it was previously deleted here without ever being read.
    //
    // Placed FIRST — ahead of the ?code= branch and the authenticated path, both of which
    // exchange the token and route to a dashboard. B2C's PKCE response carries ?code=, so a
    // check placed after that branch would never be reached.
    //
    // No token is exchanged, so no app session is
    // created — /login then purges the reset-policy MSAL cache (whose token would 400 against
    // the signin policy) and sends the user to B2C to authenticate.
    let cameFromSetPassword = false;
    try {
      cameFromSetPassword = localStorage.getItem('facilon.setpassword.inProgress') === '1';
    } catch { /* storage blocked — fall through to the normal path */ }
    if (cameFromSetPassword) {
      console.log('[LoginCallback] BOUNCE: setpassword localStorage marker present — replacing to /login?passwordSet=1');
      processedRef.current = true;
      try {
        localStorage.removeItem('facilon.setpassword.inProgress');
        Object.keys(localStorage)
          .filter((k) => k.includes('interaction.status'))
          .forEach((k) => localStorage.removeItem(k));
      } catch { /* best effort */ }
      window.location.replace('/login?passwordSet=1');
      return;
    }

    // OAuth2 code flow: URL has ?code= (e.g. from backend login URL redirect)
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (code) {
      processedRef.current = true;
      const handleCodeFlow = async () => {
        try {
          await authenticationService.handleAzureB2CCallback(code, state || undefined);
          checkAuth();
          setStatus('success');
          const roles = authenticationService.getUserRoles();
          window.location.replace(getRoleBasedDashboard(roles));
        } catch (err: any) {
          setStatus('error');
          setErrorMsg(err.response?.data?.error || err.response?.data?.message || err.message || 'Authentication failed.');
          processedRef.current = false;
        }
      };
      handleCodeFlow();
      return;
    }
    
    if (inProgress !== InteractionStatus.None) {
      console.log('[LoginCallback] Waiting for MSAL to complete redirect, inProgress:', inProgress);
      return;
    }

    // Second, storage-independent guard (2026-09-08): tokens minted by the B2C
    // PASSWORD-RESET policy name it in their tfp/acr claim (e.g. B2C_1_PASSWORDRESET, or
    // B2C_1_2resetpassword in UAT -- so match both word orders).
    // This catches every entry the localStorage marker cannot: B2C's own "Forgot
    // password?" link on the sign-in page, the set-password link opened in a different
    // browser/tab than the one that set the flag, or cleared storage. Without it those
    // paths exchange the reset-policy token below and land on the dashboard.
    const acctForPolicy = instance.getAllAccounts()[0];
    const mintingPolicy = String(
      (acctForPolicy?.idTokenClaims as Record<string, unknown> | undefined)?.tfp
      ?? (acctForPolicy?.idTokenClaims as Record<string, unknown> | undefined)?.acr
      ?? ''
    );
    console.log('[LoginCallback] minting policy claim (tfp/acr):', mintingPolicy || '(none)');
    if (/password_?reset|reset_?password/i.test(mintingPolicy)) {
      console.log('[LoginCallback] BOUNCE: token minted by reset-password policy:', mintingPolicy, '— routing to sign-in');
      processedRef.current = true;
      window.location.replace('/login?passwordSet=1');
      return;
    }

    if (!isAuthenticated) {
      console.log('[LoginCallback] MSAL redirect complete but not authenticated');

      // Detect a return from the B2C password-reset policy. That flow's tokens
      // typically don't carry the claims MSAL needs to build an account, so
      // handleRedirectPromise resolves with no account. Instead of showing
      // "Authentication failed", route the user to /login so they can sign in
      // with the password they just set.
      const hashOrSearch = (window.location.hash || '') + (window.location.search || '');
      const isPasswordResetReturn = /password_?reset|reset_?password/i.test(hashOrSearch);

      // Also clean up the setpassword in-progress flag and any leftover MSAL
      // interaction state so the user can sign in cleanly.
      try {
        localStorage.removeItem('facilon.setpassword.inProgress');
        Object.keys(localStorage)
          .filter((k) => k.includes('interaction.status'))
          .forEach((k) => localStorage.removeItem(k));
      } catch {}

      if (isPasswordResetReturn) {
        console.log('[LoginCallback] BOUNCE: not authenticated and URL names the reset policy — hash/search:', hashOrSearch.slice(0, 300));
        processedRef.current = true;
        // Use the same query param Login.tsx already handles — it clears MSAL
        // cache for the (different) reset-policy account and shows a toast.
        window.location.replace('/login?passwordSet=1');
        return;
      }
      setStatus('error');
      setErrorMsg('Authentication failed. Please try again.');
      return;
    }

    console.log('[LoginCallback] All checks passed, marking as processed');
    processedRef.current = true;
    sessionStorage.removeItem('msal_redirect_in_progress');

    const handleCallback = async () => {
      try {
        console.log('[LoginCallback] handleCallback START');

        const accounts = instance.getAllAccounts();
        console.log('[LoginCallback] MSAL accounts count:', accounts.length);

        if (accounts.length === 0) {
          console.error('[LoginCallback] No MSAL accounts found!');
          setStatus('error');
          setErrorMsg('No MSAL account found after redirect. Please try again.');
          processedRef.current = false;
          return;
        }

        const account = accounts[0];
        console.log('[LoginCallback] Using account - username:', account.username, 'homeAccountId:', account.homeAccountId);

        // Use the ID token that MSAL already extracted from the redirect response (it lives
        // on `account.idToken` after `handleRedirectPromise` resolves). Calling
        // acquireTokenSilent here is wrong: it triggers a hidden iframe refresh, which loads
        // our SPA + Bootstrap inside the iframe, and that's slower than MSAL's iframe
        // timeout — hence the `BrowserAuthError: timed_out` we were getting.
        let idToken: string | undefined = (account as any).idToken;

        if (!idToken) {
          // Last resort: try silent acquire. May still time out, but at least the user gets
          // a chance if MSAL forgot to populate account.idToken.
          console.warn('[LoginCallback] account.idToken missing; falling back to acquireTokenSilent');
          try {
            const tokenResponse = await instance.acquireTokenSilent({
              ...loginRequest,
              account,
              forceRefresh: false,
            });
            idToken = tokenResponse?.idToken;
          } catch (silentErr) {
            console.warn('[LoginCallback] acquireTokenSilent fallback failed:', silentErr);
          }
        }

        if (idToken) {
          console.log('[LoginCallback] Calling backend with idToken (length:', idToken.length, ')');
          await authenticationService.handleAzureB2CCallbackWithMSAL(idToken);

          console.log('[LoginCallback] Backend call SUCCESS, calling checkAuth()...');
          checkAuth();

          setStatus('success');
          const roles = authenticationService.getUserRoles();
          const destination = getRoleBasedDashboard(roles);
          console.log('[LoginCallback] Redirecting to:', destination);
          window.location.replace(destination);
        } else {
          console.error('[LoginCallback] No ID token available after redirect');
          setStatus('error');
          setErrorMsg('No ID token received from MSAL — please try signing in again.');
          processedRef.current = false;
        }
      } catch (err: any) {
        console.error('[LoginCallback] handleCallback ERROR:', err);
        console.error('[LoginCallback] Error details:', {
          message: err.message,
          response: err.response?.data,
          stack: err.stack
        });
        setStatus('error');
        setErrorMsg(err.response?.data?.error || err.response?.data?.message || err.message || 'Authentication failed');
        processedRef.current = false;
      }
    };

    console.log('[LoginCallback] Calling handleCallback()...');
    handleCallback();
  }, [instance, inProgress, isAuthenticated, navigate, checkAuth]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: '48px' }}>
        {status === 'loading' && <p>Completing sign in...</p>}
        {status === 'success' && <p>Sign in successful! Redirecting...</p>}
        {status === 'error' && (
          <>
            <p style={{ color: 'red' }}>{errorMsg}</p>
            <button onClick={() => navigate('/login')}>Back to Login</button>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginCallback;
